import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  FormHelperText,
  Tooltip,
  Snackbar,
  IconButton,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { Website, WebsiteFormData } from '../../../types/website.types';
import { StudioConfig, CoHostPersonality } from '../../../types/studio.types';
import { createWebsite, updateWebsite } from '../slices/websitesSlice';
// Removed getPublishers import as publisher selection UI is no longer in this form
import { Publisher, PublisherFormData } from '../../../types/publisher.types';
import { getPublisherById } from '../../publishers/api';
import { getWebsitesByPublisher } from '../api';
import ContentCollectionTab from './ContentCollectionTab';
import StudioTab from './StudioTab';
import StudioPreviewDialog from './StudioPreviewDialog';
import TabPanel, { a11yProps } from './TabPanel';
import SmartLogo from '../../../components/common/SmartLogo';
import { validateUrl } from '../../../utils/validation';
import InfoIcon from '@mui/icons-material/Info';
import ArticleIcon from '@mui/icons-material/Article';
import MicIcon from '@mui/icons-material/Mic';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ConfirmDialog from './common/ConfirmDialog';
import isEqual from 'lodash/isEqual';
import { updatePublisher } from '../../publishers/slices/publishersSlice';
import IssueCastTab from './IssueCastTab';
import CollapsibleSection from '../../../components/common/CollapsibleSection';
import ImageUrlSelector from '../../../components/common/ImageUrlSelector';
import LocationSelector from 'features/locations/LocationSelector';
import ExpandableTextField from './common/ExpandableTextField';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AIAssistantIcon from './common/AIAssistantIcon';
import { useNavigate } from 'react-router-dom';

const WEBSITE_FORM_TAB_KEY = 'websiteForm_lastTab';

const isValidSubdomain = (value: string): boolean => {
  if (!value) return true;
  const re = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
  return re.test(value);
};

export interface EnhancedWebsiteFormData extends WebsiteFormData {
  status?: string;
  scrapingStrategy?: 'dom' | 'headless' | 'rss' | 'apikey' | 'email';
  scrapingFrequencyHours?: number;
  headlinesUrl?: string;
  selectors?: {
    articleContainer?: string;
    headline?: string;
    subheadline?: string;
    content?: string;
    publishDate?: string;
    author?: string;
    image?: string;
    category?: string;
    fullArticleContent?: string;
    fullArticleImage?: string;
  };
  rssUrl?: string;
  headlessAuth?: {
    username?: string;
    password?: string;
  };
  apiKey?: string;
  emailAddress?: string;
  studioConfig?: StudioConfig;
  demographics: string;
  timezone?: string; // New field for timezone
  issueCast?: {
    enabled: boolean;
    masthead?: {
      publisherLogoURL?: string;
      publisherSubdomain?: string;
      locationName?: string;
      backgroundGradient?: string;
    };
    sponsor?: {
      imageUrl?: string;
      mention?: string;
      clickThroughUrl?: string;
      name?: string;
    };
    weather?: {
      enabled: boolean;
      locationName?: string;
      latitude?: string | number;
      longitude?: string | number;
    };
    events?: {
      enabled: boolean;
      interests?: string;
      usePublisherEvents?: boolean;
      useSearchEvents?: boolean;
    };
    convai?: {
      enabled?: boolean;
      voiceId?: string;
      firstMessage?: string;
    };
  };
}

const initialFormData: EnhancedWebsiteFormData = {
  name: '',
  url: '',
  description: '',
  tagline: '',
  logoUrl: '',
  publisherPK: '',
  locationPK: '',
  categories: [],
  status: 'OFFLINE',
  scrapingStrategy: 'dom',
  scrapingFrequencyHours: 6,
  headlinesUrl: '',
  websitePK: '',
  selectors: {
    articleContainer: '',
    headline: '',
    subheadline: '',
    content: '',
    publishDate: '',
    author: '',
    image: '',
    category: '',
    fullArticleContent: '',
    fullArticleImage: '',
  },
  rssUrl: '',
  headlessAuth: {
    username: '',
    password: '',
  },
  apiKey: '',
  emailAddress: '',
  studioConfig: {
    format: 'cohost',
    hostIdentification: 'named',
    coHosts: [
      {
        name: '',
        nickname: '',
        gender: '',
        pronouns: '',
        age: '',
        background: '',
        personalityTraits: '',
        speakingStyle: '',
        voiceType: 'castingDirector',
      } as CoHostPersonality,
      {
        name: '',
        nickname: '',
        gender: '',
        pronouns: '',
        age: '',
        background: '',
        personalityTraits: '',
        speakingStyle: '',
        voiceType: 'castingDirector',
      } as CoHostPersonality
    ],
    newsAttribution: '',
    sponsor: '',
    premiumUpsellOffer: '',
    editorialStyle: '',
    demographics: '',
    introMusic: '',
    outroMusic: '',
    endUserProfile: {
      name: '',
      interests: [],
      preferredContentTypes: [],
      location: '',
      age: '',
      gender: '',
      backgroundDescription: '',
      conversationalInformality: 5,
    },
    relationshipDynamics: '',
    listenerSettings: {
      mood: 'Neutral',
      soundscape: 'Silence',
      informality: 5
    },
    readingStyle: 'podcast'
  } as StudioConfig,
  demographics: '',
  issueCast: {
    enabled: false,
    masthead: {
      publisherLogoURL: '',
      publisherSubdomain: '',
      locationName: '',
      backgroundGradient: '',
    },
    sponsor: {
      imageUrl: '',
      mention: '',
      clickThroughUrl: '',
      name: '',
    },
    weather: {
      enabled: false,
      locationName: '',
      latitude: '',
      longitude: ''
    },
    events: {
      enabled: false,
      interests: '',
      usePublisherEvents: false,
      useSearchEvents: false,
    },
    convai: {
      enabled: false,
      voiceId: '',
      firstMessage: "What's up?",
    }
  }
};

interface WebsiteFormProps {
  open: boolean;
  onClose: () => void;
  website?: Website;
  isEdit?: boolean;
  publisherId?: string;
  initialTab?: number;
}

const availableCategories = [
  'news', 'entertainment', 'sports', 'politics', 'business',
  'technology', 'health', 'lifestyle', 'education', 'arts', 'travel'
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'none', label: 'No local time zone (national/special interest)' },
];

const WebsiteForm: React.FC<WebsiteFormProps> = ({
  open,
  onClose,
  website,
  isEdit = false,
  publisherId,
  initialTab,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<EnhancedWebsiteFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPublisher, setSelectedPublisher] = useState<Publisher | null>(null);
  const [existingWebsites, setExistingWebsites] = useState<Website[]>([]);
  const [dirty, setDirty] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [initialData, setInitialData] = useState<EnhancedWebsiteFormData>(initialFormData);
  const [showStudioPreview, setShowStudioPreview] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  
  // Initialize tabValue from localStorage if no initialTab is provided
  const getInitialTab = useCallback((): number => {
    if (typeof initialTab === 'number') {
      return initialTab;
    }
    try {
      const saved = localStorage.getItem(WEBSITE_FORM_TAB_KEY);
      if (saved !== null) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && parsed >= 0 && parsed <= 3) {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error reading tab from localStorage:', error);
    }
    return 0;
  }, [initialTab]);
  
  const [tabValue, setTabValue] = useState(getInitialTab());

  // Restore tab from localStorage or initialTab when form opens
  useEffect(() => {
    if (open) {
      setTabValue(getInitialTab());
    }
  }, [open, getInitialTab]);

  const setNestedFormValue = (path: string, value: any) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData: any = { ...prev };
      let curr: any = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        curr[k] = curr[k] !== undefined ? { ...curr[k] } : {};
        curr = curr[k];
      }
      curr[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const fetchPublisherDetails = useCallback(async (id: string, opts?: { initializeFormIssueCast?: boolean }) => {
    try {
      const publisher = await getPublisherById(id);
      setSelectedPublisher(publisher);
      setFormData(prev => {
        const next: EnhancedWebsiteFormData = {
          ...prev,
          // Prefill basic fields if empty
          name: prev.name || publisher.name || '',
          url: prev.url || publisher.websiteUrl || '',
          tagline: prev.tagline || publisher.tagline || '',
          logoUrl: prev.logoUrl || publisher.logoUrl || '',
          publisherPK: prev.publisherPK || (publisher.id ? `PUBLISHER#${publisher.id}` : ''),
          locationPK: prev.locationPK || publisher.primaryLocationPK || '',
          issueCast:
            (publisher.issueCast as EnhancedWebsiteFormData['issueCast']) ||
            prev.issueCast ||
            { enabled: false, masthead: { publisherLogoURL: '', publisherSubdomain: '', locationName: '', backgroundGradient: '' }, sponsor: { imageUrl: '', mention: '', clickThroughUrl: '', name: '' }, weather: { enabled: false, locationName: '', latitude: '', longitude: '' }, events: { enabled: false, interests: '', usePublisherEvents: false, useSearchEvents: false }, convai: { enabled: false, voiceId: '', firstMessage: '' } },
        };
        if (opts?.initializeFormIssueCast) {
          setInitialData(next);
        }
        return next;
      });
    } catch (error) {
      console.error('Failed to fetch publisher details:', error);
    }
  }, []);

  const fetchExistingWebsites = useCallback(async (publisherId: string) => {
    try {
      const websites = await getWebsitesByPublisher(publisherId);
      setExistingWebsites(websites.filter(w => !isEdit || w.id !== website?.id));
    } catch (error) {
      console.error('Failed to fetch existing websites:', error);
    }
  }, [isEdit, website]);

  const navigateToPublisherDetail = useCallback(() => {
    // Prefer existing website's publisher when editing
    const pkFromWebsite = website?.publisherPK;
    // Otherwise use current form state
    const pkFromForm = formData.publisherPK;
    // Or selectedPublisher id
    const selectedId = selectedPublisher?.id ? `PUBLISHER#${selectedPublisher.id}` : '';

    const pk = pkFromWebsite || pkFromForm || selectedId || '';
    const id = pk.startsWith('PUBLISHER#') ? pk.split('#')[1] : (selectedPublisher?.id || '');

    if (id) {
      navigate(`/publishers/${id}`);
    }
  }, [navigate, website?.publisherPK, formData.publisherPK, selectedPublisher?.id]);

  useEffect(() => {
    if (isEdit && website) {
      const newFormData = { ...initialFormData };

      newFormData.websitePK = website.id.startsWith('WEBSITE#') ? website.id : `WEBSITE#${website.id}`;
      newFormData.name = website.name;
      newFormData.url = website.url;
      newFormData.description = website.description || '';
      newFormData.tagline = website.tagline || '';
      newFormData.logoUrl = website.logoUrl || '';
      newFormData.publisherPK = website.publisherPK;
      newFormData.locationPK = website.locationPK || '';
      newFormData.categories = website.categories || [];
      newFormData.status = website.status || 'OFFLINE';
      newFormData.demographics = website.demographics || '';
      newFormData.timezone = website.timezone || ''; // Initialize timezone from existing data

      if ('headlinesUrl' in website) {
        newFormData.headlinesUrl = (website as any).headlinesUrl as string;
      }

      if ('scrapingStrategy' in website) {
        newFormData.scrapingStrategy = (website as any).scrapingStrategy as 'dom' | 'headless' | 'rss';
      }

      if ('scrapingFrequencyHours' in website) {
        newFormData.scrapingFrequencyHours = (website as any).scrapingFrequencyHours as number;
      }

      if ('selectors' in website) {
        newFormData.selectors = { ...initialFormData.selectors, ...(website as any).selectors };
      }

      if ('rssUrl' in website) {
        newFormData.rssUrl = (website as any).rssUrl as string;
      }

      if ('headlessAuth' in website) {
        newFormData.headlessAuth = { ...initialFormData.headlessAuth, ...(website as any).headlessAuth };
      }

      if ('apiKey' in website) {
        newFormData.apiKey = (website as any).apiKey as string;
      }

      if ('emailAddress' in website) {
        newFormData.emailAddress = (website as any).emailAddress as string;
      }

      if ('studioConfig' in website && (website as any).studioConfig) {
        const sc = (website as any).studioConfig as StudioConfig;
        newFormData.studioConfig = {
          ...initialFormData.studioConfig,
          ...sc,
          coHosts: sc.coHosts?.length
            ? sc.coHosts
            : initialFormData.studioConfig?.coHosts || [],
          endUserProfile: {
            name: sc.endUserProfile?.name || '',
            interests: sc.endUserProfile?.interests || [],
            preferredContentTypes: sc.endUserProfile?.preferredContentTypes || [],
            location: sc.endUserProfile?.location || '',
            age: sc.endUserProfile?.age || '',
            gender: sc.endUserProfile?.gender || '',
            backgroundDescription: sc.endUserProfile?.backgroundDescription || '',
            conversationalInformality: sc.endUserProfile?.conversationalInformality ?? 5
          },
          readingStyle: sc.readingStyle || 'podcast'
        } as StudioConfig;
      }

      if ('issueCast' in website) {
        newFormData.issueCast = {
          enabled: Boolean((website as any).issueCast?.enabled),
          masthead: {
            publisherLogoURL: (website as any).issueCast?.masthead?.publisherLogoURL || '',
            publisherSubdomain: (website as any).issueCast?.masthead?.publisherSubdomain || '',
            locationName: (website as any).issueCast?.masthead?.locationName || '',
            backgroundGradient: (website as any).issueCast?.masthead?.backgroundGradient || '',
          },
          sponsor: {
            imageUrl: (website as any).issueCast?.sponsor?.imageUrl || '',
            mention: (website as any).issueCast?.sponsor?.mention || '',
            clickThroughUrl: (website as any).issueCast?.sponsor?.clickThroughUrl || '',
            name: (website as any).issueCast?.sponsor?.name || '',
          },
          weather: {
            enabled: Boolean((website as any).issueCast?.weather?.enabled) || false,
            locationName: (website as any).issueCast?.weather?.locationName || '',
            latitude: (website as any).issueCast?.weather?.latitude ?? '',
            longitude: (website as any).issueCast?.weather?.longitude ?? ''
          },
          events: {
            enabled: Boolean((website as any).issueCast?.events?.enabled) || false,
            interests: (website as any).issueCast?.events?.interests || '',
            usePublisherEvents: Boolean((website as any).issueCast?.events?.usePublisherEvents) || false,
            useSearchEvents: Boolean((website as any).issueCast?.events?.useSearchEvents) || false,
          },
          convai: {
            enabled: Boolean((website as any).issueCast?.convai?.enabled) || false,
            voiceId: (website as any).issueCast?.convai?.voiceId || '',
            firstMessage: (website as any).issueCast?.convai?.firstMessage || '',
          }
        };
      }

      setFormData(newFormData);
      setInitialData(newFormData);

      // Validate required fields immediately when editing existing website
      const validationErrors: Record<string, string> = {};
      
      // Check required fields
      if (!newFormData.name) validationErrors['name'] = 'Website name is required';
      if (!newFormData.url) validationErrors['url'] = 'Website URL is required';
      if (!newFormData.logoUrl) validationErrors['logoUrl'] = 'Logo URL is required';
      if (!newFormData.demographics) validationErrors['demographics'] = 'Demographics are required';
      if (!newFormData.timezone) validationErrors['timezone'] = 'Time zone is required';

      // Check URL format
      if (newFormData.url && !validateUrl(newFormData.url)) {
        validationErrors['url'] = 'Please enter a valid URL';
      }
      if (newFormData.logoUrl && !validateUrl(newFormData.logoUrl)) {
        validationErrors['logoUrl'] = 'Please enter a valid URL';
      }

      setErrors(validationErrors);

      if (website.publisherPK) {
        const publisherId = website.publisherPK.split('#')[1];
        fetchPublisherDetails(publisherId, { initializeFormIssueCast: true });
        fetchExistingWebsites(publisherId);
      }
    } else {
      const newData = {
        ...initialFormData,
        publisherPK: publisherId ? `PUBLISHER#${publisherId}` : '',
      };
      setFormData(newData);
      setInitialData(newData);

      if (publisherId) {
        fetchPublisherDetails(publisherId, { initializeFormIssueCast: true });
        fetchExistingWebsites(publisherId);
      }
    }
  }, [isEdit, website, publisherId, fetchPublisherDetails, fetchExistingWebsites]);

  useEffect(() => {
    setDirty(!isEqual(formData, initialData));
  }, [formData, initialData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Save the tab preference to localStorage
    try {
      localStorage.setItem(WEBSITE_FORM_TAB_KEY, newValue.toString());
    } catch (error) {
      console.error('Error saving tab to localStorage:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const processedValue = name === 'issueCast.masthead.publisherSubdomain' ? value.trim() : value;

    if (name.includes('.')) {
      setNestedFormValue(name, processedValue);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: processedValue,
      }));
    }

    if (errors[name]) {
      const updatedErrors = { ...errors };
      delete updatedErrors[name];
      setErrors(updatedErrors);
    }

    if (name === 'issueCast.masthead.publisherSubdomain') {
      setErrors(prev => {
        const next = { ...prev };
        const v = processedValue;
        if (v && !isValidSubdomain(v)) {
          next[name] = 'Invalid subdomain. Use 1-63 lowercase letters or numbers; hyphens allowed, but not at start or end.';
        } else {
          delete next[name];
        }
        return next;
      });
    }
  };

  const handleSelectorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const selectorName = name.replace('selectors.', '');

    setFormData(prev => ({
      ...prev,
      selectors: {
        ...prev.selectors,
        [selectorName]: value,
      },
    }));

    if (errors[name]) {
      const updatedErrors = { ...errors };
      delete updatedErrors[name];
      setErrors(updatedErrors);
    }
  };

  const handleHeadlessAuthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const fieldName = name.replace('headlessAuth.', '');

    setFormData(prev => ({
      ...prev,
      headlessAuth: {
        ...prev.headlessAuth,
        [fieldName]: value,
      },
    }));
  };

  const handleCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;

    setFormData(prev => ({
      ...prev,
      categories: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleScrapingStrategyChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as 'dom' | 'headless' | 'rss' | 'apikey' | 'email';

    setFormData(prev => ({
      ...prev,
      scrapingStrategy: value,
    }));
  };

  const handleFrequencyChange = (event: SelectChangeEvent<number>) => {
    const value = event.target.value as number;

    setFormData(prev => ({
      ...prev,
      scrapingFrequencyHours: value,
    }));
  };

  const handleTimezoneChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;

    setFormData(prev => ({
      ...prev,
      timezone: value,
    }));

    // Clear timezone error if it exists
    if (errors['timezone']) {
      const updatedErrors = { ...errors };
      delete updatedErrors['timezone'];
      setErrors(updatedErrors);
    }
  };

  const handleStudioConfigChange = (config: StudioConfig) => {
    setFormData(prev => ({
      ...prev,
      studioConfig: config,
    }));
  };

  const handleTestStudioConfig = async () => {
    setShowStudioPreview(true);
  };

  const handleCopyDemographicsPrompt = () => {
    const websiteUrl = formData.url || '[website URL]';
    const websiteName = formData.name || '[website name]';

    const prompt = `Deep Research prompt for Demographic Research\n\nProduce a demographic profile for the website ${websiteUrl} following this format:\n# DEMOGRAPHIC PROFILE: ${websiteName}\n\n\n\n## PRIMARY AUDIENCE SEGMENTS\n[Identify 2-3 core audience segments with percentage estimates]\n\n\n\n## AGE DISTRIBUTION\n- Dominant Age Range: [e.g., 45-54 years]\n- Secondary Age Range: [e.g., 35-44 years]\n- Age Concentration Score: [1-10 scale, where 10 = extremely concentrated in one age group]\n\n\n\n## GEOGRAPHIC IDENTITY\n- Primary Location: [City/Region, e.g., San Francisco]\n- Neighborhood Focus: [If applicable, e.g., Mission District]\n- Local Identity Strength: [1-10 scale, where 10 = extremely locally focused]\n- Notable Local References: [Local landmarks, institutions, events frequently referenced]\n\n\n\n## DEMOGRAPHIC COMPOSITION\n- Gender Distribution: [% Male, % Female]\n- Dominant Ethnic Groups: [List with approximate percentages]\n- Languages: [Primary, Secondary, with % of audience if available]\n- Income Level: [Category + median if available]\n- Education Level: [Dominant category]\n\n\n\n## CULTURAL ATTRIBUTES\n- Political Orientation: [Progressive/Conservative/Mixed + intensity score]\n- Key Values: [3-5 core values of the readership]\n- Professional Industries: [Common industries/professions]\n- Cultural Interests: [Music, arts, food preferences if discernible]\n\n\n\n## CONTENT ENGAGEMENT PATTERNS\n- Primary Content Types: [News, Investigations, Features, etc.]\n- Engagement Style: [Active commenters, passive readers, etc.]\n- Community Participation: [High/Medium/Low + examples]\n- Digital Fluency: [1-10 scale]`;

    navigator.clipboard
      .writeText(prompt)
      .then(() => setSnackbarOpen(true))
      .catch((err) => console.error('Failed to copy prompt:', err));
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;

    setFormData(prev => ({
      ...prev,
      status: checked ? 'ACTIVE' : 'OFFLINE',
    }));
  };

  const handleSelectorsChange = (newSelectors: Record<string, string>) => {
    setFormData(prevData => ({
      ...prevData,
      selectors: {
        ...prevData.selectors,
        ...newSelectors,
      },
    }));
  };

  const handleIssueCastEnabledChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setFormData(prev => ({
      ...prev,
      issueCast: {
        ...(
          prev.issueCast ||
          {
            masthead: { publisherLogoURL: '', publisherSubdomain: '', locationName: '' },
            sponsor: { imageUrl: '', mention: '', clickThroughUrl: '', name: '' },
            weather: { enabled: false, locationName: '', latitude: '', longitude: '' },
            events: { enabled: false, interests: '', usePublisherEvents: false, useSearchEvents: false },
            convai: { enabled: false, voiceId: '', firstMessage: '' },
          }
        ),
        enabled: checked,
      }
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.name) newErrors['name'] = 'Website name is required';
    if (!formData.url) newErrors['url'] = 'Website URL is required';
    if (!formData.logoUrl) newErrors['logoUrl'] = 'Logo URL is required';
    if (!formData.demographics) newErrors['demographics'] = 'Demographics are required';
    if (!formData.timezone) newErrors['timezone'] = 'Time zone is required';

    // Format validations
    if (formData.url && !validateUrl(formData.url)) {
      newErrors['url'] = 'Please enter a valid URL';
    }
    if (formData.logoUrl && !validateUrl(formData.logoUrl)) {
      newErrors['logoUrl'] = 'Please enter a valid URL';
    }

    // Duplicate URL per publisher
    if (formData.url && formData.publisherPK && existingWebsites.some(w => w.url.toLowerCase() === formData.url.toLowerCase())) {
      newErrors['url'] = 'This URL already exists for this publisher';
    }

    // IssueCast subdomain validation
    const subdomain = formData.issueCast?.masthead?.publisherSubdomain?.trim() || '';
    if (formData.issueCast?.enabled && subdomain && !isValidSubdomain(subdomain)) {
      newErrors['issueCast.masthead.publisherSubdomain'] = 'Invalid subdomain. Use 1-63 lowercase letters or numbers; hyphens allowed, but not at start or end.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (shouldClose: boolean = true) => {
    if (!validateForm()) {
      // Check if there are errors in Basic Information required fields
      const hasBasicInfoErrors = Object.keys(errors).some(key =>
        key === 'name' || key === 'url' || key === 'logoUrl' || key === 'demographics' || key === 'timezone'
      );

      // If Basic Information has errors, switch to that tab
      if (hasBasicInfoErrors && tabValue !== 0) {
        setTabValue(0);
        // Save to localStorage
        try {
          localStorage.setItem(WEBSITE_FORM_TAB_KEY, '0');
        } catch (error) {
          console.error('Error saving tab to localStorage:', error);
        }
        return;
      }

      const hasContentCollectionErrors = Object.keys(errors).some(key =>
        key.startsWith('selectors.') || key === 'rssUrl' || key.startsWith('headlessAuth.')
      );

      // Content Collection tab is now index 3
      if (hasContentCollectionErrors && tabValue !== 3) {
        setTabValue(3);
        return;
      }

      const hasStudioErrors = Object.keys(errors).some(key =>
        key.startsWith('studioConfig.')
      );

      // Studio tab is now index 1
      if (hasStudioErrors && tabValue !== 1) {
        setTabValue(1);
      }

      return;
    }

    setIsLoading(true);

    try {
      if (selectedPublisher) {
        const previousIssueCast = selectedPublisher.issueCast || { enabled: false, masthead: { publisherLogoURL: '', publisherSubdomain: '', locationName: '' }, sponsor: { imageUrl: '', mention: '', clickThroughUrl: '', name: '' }, weather: { enabled: false, locationName: '', latitude: '', longitude: '' }, events: { enabled: false, interests: '', usePublisherEvents: false, useSearchEvents: false }, convai: { enabled: false, voiceId: '', firstMessage: '' } };
        if (!isEqual(previousIssueCast, formData.issueCast)) {
          const publisherPayload: PublisherFormData = {
            name: selectedPublisher.name,
            tagline: selectedPublisher.tagline || '',
            businessAddress: {
              street: selectedPublisher.businessAddress?.street || '',
              city: selectedPublisher.businessAddress?.city || '',
              state: selectedPublisher.businessAddress?.state || '',
              zip: selectedPublisher.businessAddress?.zip || '',
            },
            logoUrl: selectedPublisher.logoUrl || '',
            websiteUrl: selectedPublisher.websiteUrl || '',
            foundedYear: selectedPublisher.foundedYear || new Date().getFullYear(),
            primaryLocationPK: selectedPublisher.primaryLocationPK || '',
            issueCast: formData.issueCast,
          };
          await dispatch(updatePublisher({ id: selectedPublisher.id, data: publisherPayload }));
        }
      }

      const submissionData = {
        ...formData,
        ...(formData.scrapingStrategy !== 'dom' && { selectors: undefined }),
        ...(formData.scrapingStrategy !== 'rss' && { rssUrl: undefined }),
        ...(formData.scrapingStrategy !== 'headless' && { headlessAuth: undefined }),
        ...(formData.scrapingStrategy !== 'apikey' && { apiKey: undefined }),
        ...(formData.scrapingStrategy !== 'email' && { emailAddress: undefined }),
      } as EnhancedWebsiteFormData;

      if (isEdit && website) {
        await dispatch(updateWebsite({ id: website.id, data: submissionData }));
      } else {
        await dispatch(createWebsite(submissionData));
      }
      
      // After successful save, update initialData to match current formData
      // This makes the form no longer dirty
      if (!shouldClose) {
        setInitialData(formData);
      }
      
      if (shouldClose) {
        onClose();
        navigateToPublisherDetail();
      }
    } catch (error) {
      console.error('Error saving website:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAndClose = () => {
    handleSubmit(true);
  };

  const handleSave = () => {
    handleSubmit(false);
  };

  const handleRequestClose = () => {
    if (dirty) {
      setShowConfirmDialog(true);
    } else {
      onClose();
      navigateToPublisherDetail();
    }
  };

  const handleConfirmDiscard = () => {
    setShowConfirmDialog(false);
    if (isEdit && website) {
      const newFormData = { ...initialFormData };
      newFormData.websitePK = website.id.startsWith('WEBSITE#') ? website.id : `WEBSITE#${website.id}`;
      newFormData.name = website.name;
      newFormData.url = website.url;
      newFormData.description = website.description || '';
      newFormData.tagline = website.tagline || '';
      newFormData.logoUrl = website.logoUrl || '';
      newFormData.publisherPK = website.publisherPK;
      newFormData.locationPK = website.locationPK || '';
      newFormData.categories = website.categories || [];
      newFormData.status = website.status || 'OFFLINE';
      newFormData.demographics = website.demographics || '';
      newFormData.timezone = website.timezone || '';
      if ('headlinesUrl' in website) newFormData.headlinesUrl = (website as any).headlinesUrl as string;
      if ('scrapingStrategy' in website) newFormData.scrapingStrategy = (website as any).scrapingStrategy as 'dom' | 'headless' | 'rss';
      if ('scrapingFrequencyHours' in website) newFormData.scrapingFrequencyHours = (website as any).scrapingFrequencyHours as number;
      if ('selectors' in website) newFormData.selectors = { ...initialFormData.selectors, ...(website as any).selectors };
      if ('rssUrl' in website) newFormData.rssUrl = (website as any).rssUrl as string;
      if ('headlessAuth' in website) newFormData.headlessAuth = { ...initialFormData.headlessAuth, ...(website as any).headlessAuth };
      if ('apiKey' in website) newFormData.apiKey = (website as any).apiKey as string;
      if ('emailAddress' in website) newFormData.emailAddress = (website as any).emailAddress as string;
      if ('studioConfig' in website && (website as any).studioConfig) {
        const sc = (website as any).studioConfig as StudioConfig;
        newFormData.studioConfig = {
          ...initialFormData.studioConfig,
          ...sc,
          coHosts: sc.coHosts?.length ? sc.coHosts : initialFormData.studioConfig?.coHosts || [],
          endUserProfile: {
            name: sc.endUserProfile?.name || '',
            interests: sc.endUserProfile?.interests || [],
            preferredContentTypes: sc.endUserProfile?.preferredContentTypes || [],
            location: sc.endUserProfile?.location || '',
            age: sc.endUserProfile?.age || '',
            gender: sc.endUserProfile?.gender || '',
            backgroundDescription: sc.endUserProfile?.backgroundDescription || '',
            conversationalInformality: sc.endUserProfile?.conversationalInformality ?? 5
          },
          readingStyle: sc.readingStyle || 'podcast'
        } as StudioConfig;
      }
      if ('issueCast' in website) {
        newFormData.issueCast = {
          enabled: Boolean((website as any).issueCast?.enabled),
          masthead: {
            publisherLogoURL: (website as any).issueCast?.masthead?.publisherLogoURL || '',
            publisherSubdomain: (website as any).issueCast?.masthead?.publisherSubdomain || '',
            locationName: (website as any).issueCast?.masthead?.locationName || '',
            backgroundGradient: (website as any).issueCast?.masthead?.backgroundGradient || '',
          },
          sponsor: {
            imageUrl: (website as any).issueCast?.sponsor?.imageUrl || '',
            mention: (website as any).issueCast?.sponsor?.mention || '',
            clickThroughUrl: (website as any).issueCast?.sponsor?.clickThroughUrl || '',
            name: (website as any).issueCast?.sponsor?.name || '',
          },
          weather: {
            enabled: Boolean((website as any).issueCast?.weather?.enabled) || false,
            locationName: (website as any).issueCast?.weather?.locationName || '',
            latitude: (website as any).issueCast?.weather?.latitude ?? '',
            longitude: (website as any).issueCast?.weather?.longitude ?? ''
          },
          events: {
            enabled: Boolean((website as any).issueCast?.events?.enabled) || false,
            interests: (website as any).issueCast?.events?.interests || '',
            usePublisherEvents: Boolean((website as any).issueCast?.events?.usePublisherEvents) || false,
            useSearchEvents: Boolean((website as any).issueCast?.events?.useSearchEvents) || false,
          },
          convai: {
            enabled: Boolean((website as any).issueCast?.convai?.enabled) || false,
            voiceId: (website as any).issueCast?.convai?.voiceId || '',
            firstMessage: (website as any).issueCast?.convai?.firstMessage || '',
          }
        };
      }
      setFormData(newFormData);
      setInitialData(newFormData);
    } else {
      setFormData(initialFormData);
      setInitialData(initialFormData);
    }
    onClose();
    navigateToPublisherDetail();
  };

  const handleCancelDiscard = () => {
    setShowConfirmDialog(false);
  };

  // Tab validation status helpers
  const getBasicInfoTabStatus = (): 'valid' | 'warning' | 'error' => {
    const requiredFields = {
      name: formData.name,
      url: formData.url,
      logoUrl: formData.logoUrl,
      demographics: formData.demographics,
      timezone: formData.timezone,
    };

    const missingRequired = Object.entries(requiredFields).some(([_, value]) => !value);
    
    if (missingRequired) {
      return 'error';
    }

    // Check for format errors
    if (formData.url && !validateUrl(formData.url)) {
      return 'error';
    }
    if (formData.logoUrl && !validateUrl(formData.logoUrl)) {
      return 'error';
    }

    return 'valid';
  };

  const getStudioTabStatus = (): 'valid' | 'warning' | 'error' => {
    const studioConfig = formData.studioConfig;
    if (!studioConfig) return 'error';

    // Check important field: Editorial Style (Speech & Audio Style Guide)
    const hasEditorialStyle = !!(studioConfig.editorialStyle && studioConfig.editorialStyle.trim().length > 0);

    // Determine number of required co-hosts based on format
    const numRequiredHosts = studioConfig.format === 'solo' ? 1 : 2;
    const coHosts = studioConfig.coHosts || [];

    // Check required fields for each co-host
    let hasRequiredFieldsMissing = false;
    for (let i = 0; i < numRequiredHosts; i++) {
      const host = coHosts[i];
      if (!host) {
        hasRequiredFieldsMissing = true;
        break;
      }

      // Required fields: Full Name, Nickname, Gender, Age, Personality Traits, Speaking Style
      if (!host.name || !host.name.trim()) hasRequiredFieldsMissing = true;
      if (!host.nickname || !host.nickname.trim()) hasRequiredFieldsMissing = true;
      if (!host.gender || !host.gender.trim()) hasRequiredFieldsMissing = true;
      if (!host.age || !host.age.trim()) hasRequiredFieldsMissing = true;
      if (!host.personalityTraits || !host.personalityTraits.trim()) hasRequiredFieldsMissing = true;
      if (!host.speakingStyle || !host.speakingStyle.trim()) hasRequiredFieldsMissing = true;
      
      // Voice validation depends on voiceType:
      // - castingDirector: requires voiceId (generated by AI)
      // - journalist: no voiceId required (uses journalist's actual voice)
      // - custom: requires customVoiceId (user-provided)
      if (host.voiceType === 'castingDirector') {
        if (!host.voiceId || !host.voiceId.trim()) hasRequiredFieldsMissing = true;
      } else if (host.voiceType === 'custom') {
        if (!host.customVoiceId || !host.customVoiceId.trim()) hasRequiredFieldsMissing = true;
      }
      // journalist voice type requires no voice ID
    }

    // Return error if required fields are missing
    if (hasRequiredFieldsMissing) {
      return 'error';
    }

    // Return warning if important field (editorial style) is missing
    if (!hasEditorialStyle) {
      return 'warning';
    }

    return 'valid';
  };

  const getIssueCastTabStatus = (): 'valid' | 'warning' | 'error' => {
    if (!formData.issueCast?.enabled) return 'valid';

    let status: 'valid' | 'warning' | 'error' = 'valid';

    // ConvAI Validation
    if (formData.issueCast.convai?.enabled) {
      if (!formData.issueCast.convai.voiceId) {
        return 'error';
      }
      if (!formData.issueCast.convai.firstMessage?.trim()) {
        status = 'warning';
      }
    }

    return status;
  };

  const getContentCollectionTabStatus = (): 'valid' | 'warning' | 'error' => {
    // For now, always return valid
    return 'valid';
  };

  const getTabStatusIcon = (status: 'valid' | 'warning' | 'error') => {
    switch (status) {
      case 'valid':
        return (
          <Tooltip title="All fields valid" arrow>
            <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', ml: 0.5 }} />
          </Tooltip>
        );
      case 'warning':
        return (
          <Tooltip title="Missing important fields" arrow>
            <WarningIcon sx={{ fontSize: 18, color: 'warning.main', ml: 0.5 }} />
          </Tooltip>
        );
      case 'error':
        return (
          <Tooltip title="Invalid or missing required fields" arrow>
            <ErrorIcon sx={{ fontSize: 18, color: 'error.main', ml: 0.5 }} />
          </Tooltip>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleRequestClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        style: {
          height: '90vh',
          width: '90vw',
          maxHeight: '90vh',
          maxWidth: '90vw',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          {isEdit && website && (
            <Box sx={{ mr: '30px' }}>
              <SmartLogo
                logoUrl={website.logoUrl!}
                name={website.name}
              />
            </Box>
          )}
          <Typography variant="h4">
            {isEdit ? `Edit News Source: ${website?.name}` : 'Add New News Source'}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="website form tabs"
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                height: '4px',
                borderRadius: '4px',
                backgroundColor: 'primary.main',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                },
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: '600',
                },
              },
            }}
          >
            <Tab 
              icon={<InfoIcon />} 
              iconPosition="start" 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Basic Information
                  {getTabStatusIcon(getBasicInfoTabStatus())}
                </Box>
              } 
              {...a11yProps(0)} 
            />
            <Tab 
              icon={<MicIcon />} 
              iconPosition="start" 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Studio
                  {getTabStatusIcon(getStudioTabStatus())}
                </Box>
              } 
              {...a11yProps(1)} 
            />
            <Tab 
              icon={<PodcastsIcon />} 
              iconPosition="start" 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Podcast
                  {getTabStatusIcon(getIssueCastTabStatus())}
                </Box>
              } 
              {...a11yProps(2)} 
            />
            <Tab 
              icon={<ArticleIcon />} 
              iconPosition="start" 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Content Collection
                  {getTabStatusIcon(getContentCollectionTabStatus())}
                </Box>
              } 
              {...a11yProps(3)} 
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {/* Required + Optional sections inside Basic Information */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CollapsibleSection title="Required Fields" namespace="website-form" id="required" defaultExpanded>
                <Grid container spacing={2}>
                  {/* Name (required) */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      error={!!errors['name']}
                      helperText={errors['name']}
                      disabled={isLoading}
                      placeholder="e.g., SF Chronicle"
                    />
                  </Grid>

                  {/* Website URL (required) */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Website URL"
                      name="url"
                      value={formData.url}
                      onChange={handleInputChange}
                      error={!!errors['url']}
                      helperText={errors['url']}
                      disabled={isLoading}
                      placeholder="e.g., https://www.sfchronicle.com"
                    />
                  </Grid>

                  {/* Logo URL replaced with ImageUrlSelector (required) */}
                  <Grid item xs={12}>
                    <ImageUrlSelector
                      label="Logo URL"
                      name="logoUrl"
                      value={formData.logoUrl}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      publisherId={(formData.publisherPK || '').replace('PUBLISHER#','') || undefined}
                      placeholder="https://..."
                      helperText={errors['logoUrl'] || ''}
                      error={!!errors['logoUrl']}
                      required
                    />
                  </Grid>

                  {/* Demographics (required) */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1">Demographics</Typography>
                      <Box sx={{ ml: 2 }}>
                        <Tooltip title="Copy a deep research prompt to generate demographics" arrow>
                          <Button
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopyDemographicsPrompt}
                            disabled={isLoading}
                            sx={{ textTransform: 'none' }}
                          >
                            Copy deep research prompt
                          </Button>
                        </Tooltip>
                        <Tooltip title="Generate Demographics with AI">
                          <span>
                            <IconButton
                              color="primary"
                              onClick={() => alert("This operation is not yet implemented.")}
                              disabled={isLoading}
                              sx={{
                                ml: 1,
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                }
                              }}
                              aria-label="Generate with AI"
                            >
                              <AIAssistantIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                    <ExpandableTextField
                      fullWidth
                      required
                      label="Demographics"
                      name="demographics"
                      value={formData.demographics}
                      onChange={handleInputChange}
                      error={!!errors['demographics']}
                      helperText={errors['demographics']}
                      disabled={isLoading}
                      defaultRows={3}
                      expandedRows={15}
                      placeholder="Provide the demographics for the website"
                    />
                  </Grid>

                  {/* Timezone (required) */}
                  <Grid item xs={12}>
                    <FormControl fullWidth required error={!!errors['timezone']}>
                      <InputLabel id="timezone-label">Time Zone</InputLabel>
                      <Select
                        labelId="timezone-label"
                        id="timezone"
                        value={formData.timezone || ''}
                        onChange={handleTimezoneChange}
                        label="Time Zone"
                        disabled={isLoading}
                      >
                        {TIMEZONES.map((tz) => (
                          <MenuItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors['timezone'] || 'Select the primary time zone for this news source. Choose "No local time zone" for national or special interest news sources without a specific geographic focus.'}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>
              </CollapsibleSection>
            </Grid>

            <Grid item xs={12}>
              <CollapsibleSection title="Optional Fields" namespace="website-form" id="optional" defaultExpanded={false} collapsedSummary="Tagline, Description, Categories, Location">
                <Grid container spacing={2}>
                  {/* Tagline (optional) */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Tagline"
                      name="tagline"
                      value={formData.tagline}
                      onChange={handleInputChange}
                      error={!!errors['tagline']}
                      helperText={errors['tagline']}
                      disabled={isLoading}
                      placeholder="e.g., Breaking news from the SF Chronicle"
                    />
                  </Grid>

                  {/* Description (optional) */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      error={!!errors['description']}
                      helperText={errors['description']}
                      disabled={isLoading}
                      multiline
                      rows={3}
                      placeholder="Provide a brief description of the website"
                    />
                  </Grid>

                  {/* Categories (optional) */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="categories-label">Categories</InputLabel>
                      <Select
                        labelId="categories-label"
                        id="categories"
                        multiple
                        value={formData.categories}
                        onChange={handleCategoryChange}
                        input={<OutlinedInput id="select-multiple-chip" label="Categories" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} />
                            ))}
                          </Box>
                        )}
                      >
                        {availableCategories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Select categories that best describe this website's content
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* Location (optional) */}
                  <Grid item xs={12}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Location
                      </Typography>
                    </Box>
                    <LocationSelector
                      value={formData.locationPK}
                      onChange={(value) => {
                        formData.locationPK = value;
                        handleInputChange({ target: { name: 'locationPK', value } } as React.ChangeEvent<HTMLInputElement>);
                      }}
                      error={errors['locationPK']}
                      disabled={isLoading}
                    />
                    {errors['locationPK'] && (
                      <FormHelperText error>{errors['locationPK']}</FormHelperText>
                    )}
                  </Grid>
                </Grid>
              </CollapsibleSection>
            </Grid>
          </Grid>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
            message="Deep research prompt copied to clipboard! Ready to paste into Gemini or ChatGPT."
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <StudioTab
            formData={{
              studioConfig: formData.studioConfig,
              websitePK: formData.websitePK || '',
              demographics: formData.demographics,
              name: formData.name,
              locationName: formData.issueCast?.masthead?.locationName
            }}
            errors={errors}
            isLoading={isLoading}
            onStudioConfigChange={handleStudioConfigChange}
            onTestStudioConfig={handleTestStudioConfig}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <IssueCastTab
            formData={formData}
            errors={errors}
            isLoading={isLoading}
            onInputChange={handleInputChange}
            onIssueCastEnabledChange={handleIssueCastEnabledChange}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <ContentCollectionTab
            formData={formData}
            errors={errors}
            isLoading={isLoading}
            onStrategyChange={handleScrapingStrategyChange}
            onFrequencyChange={handleFrequencyChange}
            onSelectorChange={handleSelectorChange}
            onSelectorsChange={handleSelectorsChange}
            onInputChange={handleInputChange}
            onHeadlessAuthChange={handleHeadlessAuthChange}
            onStatusChange={handleStatusChange}
          />
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleRequestClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="outlined"
          color="primary"
          disabled={isLoading}
        >
          {isEdit ? 'Update' : 'Save'}
        </Button>
        <Button
          onClick={handleSaveAndClose}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isEdit ? 'Update & Close' : 'Create & Close'}
        </Button>
      </DialogActions>

      {formData.websitePK && (
        <StudioPreviewDialog
          open={showStudioPreview}
          onClose={() => setShowStudioPreview(false)}
          websiteId={formData.websitePK.replace('WEBSITE#', '')}
          studioConfig={formData.studioConfig as StudioConfig}
        />
      )}

      <ConfirmDialog
        open={showConfirmDialog}
        title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        onConfirm={handleConfirmDiscard}
        onCancel={handleCancelDiscard}
        confirmText="Discard"
        cancelText="Keep Editing"
      />
    </Dialog>
  );
};

export default WebsiteForm;
